/* Energy Unleashed — bundle draft.
 *
 * One in-progress bundle, shared by every surface that can build one: the
 * collection-page builder, the product-page quantity break, and anything added
 * later. Held in localStorage and committed to the cart exactly once.
 *
 * WHY A DRAFT RATHER THAN WRITING TINS STRAIGHT INTO THE CART
 * The obvious alternative is to add each tin as it is picked, grouped by a
 * shared `_bundle_id`, and let the cart be the state. It was rejected for two
 * concrete reasons:
 *
 *   1. Off-tier pricing is real money. Four tins toward a five bill at
 *      4 x $29.95 = $119.80, which is MORE than the five-tin tier at $109.95.
 *      A cart-as-state build is genuinely off-tier between every pick, so the
 *      cart drawer's own correction card fires at the customer mid-build,
 *      telling them they are overpaying for a bundle they have not finished.
 *   2. eu-bundle-selector.js commits atomically today, and that commit path is
 *      what cart-test.mjs, cart-test-tiers.mjs, cart-test-rewards.mjs and
 *      cart-test-labels.mjs cover. A draft leaves that path untouched — the
 *      selector gains a read and a write, and everything the suites assert
 *      about the cart still holds.
 *
 * The cost, stated plainly: a draft does not follow someone from phone to
 * laptop. Any surface that resumes one must say what it is resuming and offer
 * a way out, or a shopper meets a half-built bundle they do not remember.
 *
 * THE TIER TABLE IS A COPY, AND THE DISCOUNT FUNCTION IS THE ORIGINAL
 * Authoritative prices live in eu-bundle-function/extensions/bundle-pricing/
 * src/cart_lines_discounts_generate_run.js. If a tier moves there and not here,
 * this file will quote a saving the cart refuses to honour. 15 tins is absent
 * on purpose: it is reachable only by upgrading a 10 inside the cart drawer.
 */
(function () {
  'use strict';
  if (window.EUBundleDraft) return;   // safe if loaded more than once on a page

  var KEY = 'eu:bundle-draft';
  var EVENT = 'eu:bundle-draft';
  var MAX_AGE = 7 * 24 * 60 * 60 * 1000;

  var TIN = 2995;
  var POUCHES = 20;
  var TIERS = { 3: 6995, 5: 10995, 10: 18995 };
  var TIER_NS = [1, 3, 5, 10];

  function uuid() {
    /* Reach through `window` on both halves. Testing the guard on window.crypto
       and then calling a bare `crypto` happens to work in a browser, where they
       are the same global, and throws anywhere else. */
    var c = window.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return 'eu-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* ---------------------------------------------------------------- money -- */

  /** What n tins are actually charged. Off-tier draws nothing. */
  function priceFor(n) { return n <= 0 ? 0 : (TIERS[n] || n * TIN); }
  function perPouch(n) { return n <= 0 ? 0 : Math.round(priceFor(n) / (n * POUCHES)); }
  function savedVs(n) { return (n * TIN) - priceFor(n); }
  function nextTier(n) {
    for (var i = 0; i < TIER_NS.length; i++) if (TIER_NS[i] > n) return TIER_NS[i];
    return null;
  }

  /* ---- auto-bundle: quick-add's "one growing bundle" pricing --------------
   * A quick-added set of N tins is priced as ONE bundle at the largest tier
   * that fits, with any remainder loose at full price. Monotonic: adding a tin
   * never lowers the total, and never spikes it — 3 = $69.95, 4 = $69.95 + one
   * loose $29.95, 5 = $109.95, and so on. The floor tier is what actually gets
   * a shared _bundle_id in the cart; the remainder stays loose. */
  function autoTier(n) {
    var t = 0;
    for (var i = 0; i < TIER_NS.length; i++) if (TIER_NS[i] <= n && TIER_NS[i] > 1) t = TIER_NS[i];
    return t;
  }
  function autoPrice(n) {
    var t = autoTier(n);
    return priceFor(t) + (n - t) * TIN;   // priceFor(0) is 0
  }
  function autoSaved(n) { return n * TIN - autoPrice(n); }

  /** A larger tier that costs LESS than n tins do — 4 against 5, 7/8/9 against 10. */
  function cheaperAbove(n) {
    var p = priceFor(n);
    for (var i = 0; i < TIER_NS.length; i++) {
      if (TIER_NS[i] > n && priceFor(TIER_NS[i]) < p) return TIER_NS[i];
    }
    return null;
  }

  /* ---------------------------------------------------------------- state -- */

  function emit(draft) {
    try {
      document.dispatchEvent(new CustomEvent(EVENT, { detail: draft || null }));
    } catch (e) {
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(EVENT, false, false, draft || null);
      document.dispatchEvent(ev);
    }
  }

  function read() {
    var raw;
    try { raw = localStorage.getItem(KEY); } catch (e) { return null; }
    if (!raw) return null;
    var d;
    try { d = JSON.parse(raw); } catch (e) { clear(); return null; }
    if (!d || !d.id || !d.slots || Object.prototype.toString.call(d.slots) !== '[object Array]') {
      clear(); return null;
    }
    /* A week-old draft is not a build in progress, it is a surprise. */
    if (!d.updated || (Date.now() - d.updated) > MAX_AGE) { clear(); return null; }
    return d;
  }

  function write(d) {
    if (!d) return clear();
    d.updated = Date.now();
    try { localStorage.setItem(KEY, JSON.stringify(d)); } catch (e) { /* private mode */ }
    emit(d);
    return d;
  }

  function clear() {
    try { localStorage.removeItem(KEY); } catch (e) {}
    emit(null);
    return null;
  }

  /** Begin a build at `tier`, keeping anything already picked. */
  function start(tier, seed) {
    var prev = read();
    var kept = (prev && prev.slots) ? prev.slots.filter(function (s) { return s != null; }) : [];
    if (seed) kept = kept.concat(seed);
    var slots = [];
    for (var i = 0; i < tier; i++) slots.push(kept[i] != null ? kept[i] : null);
    return write({ id: (prev && prev.id) || uuid(), tier: tier, slots: slots });
  }

  /** Empty every slot but keep the id and tier — what "Clear" means. */
  function reset() {
    var d = read();
    if (!d) return null;
    var slots = [];
    for (var i = 0; i < d.tier; i++) slots.push(null);
    d.slots = slots;
    return write(d);
  }

  /** The most recently picked variant, or null. */
  function last(d) {
    d = d || read();
    if (!d) return null;
    for (var i = d.slots.length - 1; i >= 0; i--) if (d.slots[i] != null) return d.slots[i];
    return null;
  }

  /** Resize an existing build, keeping picks that still fit. */
  function setTier(tier) {
    var d = read();
    if (!d) return start(tier);
    var kept = d.slots.filter(function (s) { return s != null; });
    var slots = [];
    for (var i = 0; i < tier; i++) slots.push(kept[i] != null ? kept[i] : null);
    d.tier = tier; d.slots = slots;
    return write(d);
  }

  function filled(d) {
    d = d || read();
    if (!d) return 0;
    var n = 0;
    for (var i = 0; i < d.slots.length; i++) if (d.slots[i] != null) n++;
    return n;
  }

  /** Drop a variant into the first free slot. Returns the draft, or null if full. */
  function add(variantId) {
    var d = read();
    if (!d) return null;
    for (var i = 0; i < d.slots.length; i++) {
      if (d.slots[i] == null) { d.slots[i] = String(variantId); return write(d); }
    }
    return d;
  }

  /** Remove the LAST occurrence, so tapping minus undoes the most recent tap. */
  function remove(variantId) {
    var d = read();
    if (!d) return null;
    for (var i = d.slots.length - 1; i >= 0; i--) {
      if (String(d.slots[i]) === String(variantId)) { d.slots[i] = null; break; }
    }
    /* Keep the nulls at the end so slot order reads as pick order. */
    var kept = d.slots.filter(function (s) { return s != null; });
    var slots = [];
    for (var j = 0; j < d.tier; j++) slots.push(kept[j] != null ? kept[j] : null);
    d.slots = slots;
    return write(d);
  }

  function countOf(variantId, d) {
    d = d || read();
    if (!d) return 0;
    var n = 0;
    for (var i = 0; i < d.slots.length; i++) if (String(d.slots[i]) === String(variantId)) n++;
    return n;
  }

  /* --------------------------------------------------------------- commit -- */

  /**
   * Send the draft to the cart as ONE bundle: identical variants merged into a
   * single line, every line carrying the same `_bundle_id`.
   *
   * `_bundle_tier` must equal the group's total quantity — eu-cart-drawer's
   * staleBundleLine() compares exactly that, and a mismatch sets it rewriting
   * every line the moment the drawer opens. `Bundle` matches bundleLabel().
   */
  function commit() {
    var d = read();
    if (!d) return Promise.reject(new Error('no draft'));

    var picked = d.slots.filter(function (s) { return s != null; });
    if (!picked.length) return Promise.reject(new Error('draft is empty'));

    var qty = {};
    picked.forEach(function (id) { qty[id] = (qty[id] || 0) + 1; });

    var total = picked.length;
    var items = Object.keys(qty).map(function (id) {
      return {
        id: parseInt(id, 10),
        quantity: qty[id],
        properties: {
          _bundle_id: d.id,
          _bundle_tier: String(total),
          Bundle: total + ' Tin' + (total === 1 ? '' : 's')
        }
      };
    });

    var root = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');

    /* Two encodings, same as eu-bundle-selector.js: some cart apps wrap
     * /cart/add.js and only understand one of them. */
    var asForm = function () {
      var fd = new URLSearchParams();
      items.forEach(function (it, i) {
        fd.append('items[' + i + '][id]', it.id);
        fd.append('items[' + i + '][quantity]', it.quantity);
        Object.keys(it.properties).forEach(function (k) {
          fd.append('items[' + i + '][properties][' + k + ']', it.properties[k]);
        });
      });
      return fd.toString();
    };
    var attempt = function (mode) {
      return fetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: mode === 'json'
          ? { 'Content-Type': 'application/json', Accept: 'application/json' }
          : { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        body: mode === 'json' ? JSON.stringify({ items: items }) : asForm()
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || ('HTTP ' + r.status)); });
        return r.json();
      });
    };

    return attempt('json')
      .catch(function () { return attempt('form'); })
      /* An add that returned 200 is not proof the lines landed — read the cart
       * back and count them, exactly as the product-page selector does. */
      .then(function () {
        return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        var got = (cart.items || []).filter(function (l) {
          return l.properties && l.properties._bundle_id === d.id;
        }).reduce(function (a, l) { return a + l.quantity; }, 0);
        if (got !== total) {
          throw new Error('Sent ' + total + ' tins, cart came back with ' + got + '.');
        }
        return cart;
      })
      .then(function (res) {
      clear();
      /* EUCart owns the drawer; refreshing through it keeps one source of cart
       * truth rather than racing the drawer's own listeners. */
      if (window.EUCart && window.EUCart.refresh) {
        return window.EUCart.refresh().then(function () {
          if (window.EUCart.open) window.EUCart.open();
          return res;
        });
      }
      return res;
    });
  }

  /* Another tab finishing or abandoning a build has to reach this one. */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) emit(read());
  });

  window.EUBundleDraft = {
    EVENT: EVENT,
    TIN: TIN, POUCHES: POUCHES, TIERS: TIERS, TIER_NS: TIER_NS,
    read: read, write: write, clear: clear,
    start: start, setTier: setTier, reset: reset, last: last,
    add: add, remove: remove, filled: filled, countOf: countOf,
    commit: commit,
    priceFor: priceFor, perPouch: perPouch, savedVs: savedVs,
    nextTier: nextTier, cheaperAbove: cheaperAbove, autoTier: autoTier, autoPrice: autoPrice, autoSaved: autoSaved,
    money: function (cents) { return '$' + (cents / 100).toFixed(2); }
  };
})();
